import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const faqs: LearnFAQ[] = [
  {
    question: "Is deep TMS better than SAINT?",
    answer:
      "Neither is universally better. Deep TMS (BrainsWay H-coil) has a longer published evidence base for depression and is FDA-cleared for a broader set of indications. SAINT® uses fMRI-guided personalized targeting and an accelerated 5-day schedule, with published randomized trial data for major depressive disorder. The right choice depends on your diagnosis, schedule, and how much evidence you want behind the specific protocol. A free consultation is the best way to decide.",
  },
  {
    question: "Which works faster, deep TMS or SAINT?",
    answer:
      "SAINT® is faster in calendar time: about 50 sessions over 5 consecutive days. Deep TMS typically runs one session per day over 4 to 6 weeks, for roughly 36 sessions total. SAINT® compresses the same total pulse count into one week using an accelerated iTBS pattern; deep TMS spreads it out. Speed is one factor — evidence and targeting matter too.",
  },
  {
    question: "Does insurance cover deep TMS?",
    answer:
      "Deep TMS may be covered for depression; coverage varies by plan and insurer. Standard TMS has the most established insurance coverage pathway. SAINT® may also be covered; coverage varies by plan. Neuro Wellness verifies your benefits for free, whatever carrier you have, so you know what your plan allows before you commit to any protocol.",
  },
  {
    question: "What is the H-coil in deep TMS?",
    answer:
      "The H-coil is the treatment coil used in BrainsWay deep TMS. Its design allows magnetic stimulation to reach deeper into the brain than a standard figure-8 coil, which is why it is called 'deep' TMS. The coil is placed over the left dorsolateral prefrontal cortex for depression treatment. It is FDA-cleared for major depressive disorder and several other indications.",
  },
  {
    question: "Can I do SAINT if I already tried deep TMS?",
    answer:
      "Possibly. If deep TMS did not provide enough relief, SAINT® may still be an option because it uses a different targeting approach — fMRI-guided personalization rather than a standard scalp measurement. Eligibility is clinical and depends on your diagnosis, medication history, and whether you have any contraindications. A free consultation with our clinical team is the best way to find out.",
  },
];

export const pageSaintVsDeepTms = makeArticle({
  slug: "saint-vs-deep-tms",
  title: "SAINT® vs Deep TMS (BrainsWay): Speed, Cost & Remission",
  description:
    "Deep TMS uses an H-coil over ~6 weeks; SAINT® uses personalized targeting over 5 days. Compare outcomes, time, cost and side effects.",
  canonical: "/learn/saint-vs-deep-tms",
  h1: "SAINT® vs Deep TMS: Speed, Cost and Remission",
  breadcrumbLabel: "SAINT® vs Deep TMS",
  shortAnswer:
    "Deep TMS (delivered on the BrainsWay H-coil system) and SAINT® are both FDA-cleared transcranial magnetic stimulation treatments for depression, but they differ in how they target the brain, how long the course takes, and what evidence supports each. Deep TMS uses a specialized coil to stimulate deeper brain tissue over about 4 to 6 weeks. SAINT® uses an fMRI scan to personalize the treatment target and delivers 10 sessions per day for 5 consecutive days. Neither is universally better — the right choice depends on your diagnosis, schedule, and what evidence you want behind your treatment.",
  keyTakeaways: [
    "Deep TMS (BrainsWay H-coil) is FDA-cleared for major depressive disorder and other indications; it reaches deeper brain tissue and typically runs 4–6 weeks of daily sessions.",
    "SAINT® is FDA-cleared for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication; it uses fMRI-guided personalized targeting over 5 consecutive days.",
    "SAINT® at Neuro Wellness starts at $25,000; deep TMS and standard TMS may be covered — coverage varies by plan, and we verify your benefits for free.",
    "Neither protocol is a cure or guaranteed; individual results may vary. A free consultation is the best way to learn which fits your situation.",
  ],
  sections: [
    {
      heading: "Quick answer: SAINT® vs deep TMS",
      paragraphs: [
        "If you are comparing SAINT® and deep TMS, you are comparing two FDA-cleared transcranial magnetic stimulation (TMS) protocols that both treat depression but work differently. TMS uses magnetic pulses to stimulate specific areas of the brain involved in mood regulation. The two protocols differ in the coil used, how deep the stimulation reaches, how the target is chosen, and how long the treatment course takes.",
        "Deep TMS — also called dTMS — is delivered on the BrainsWay system using a specialized H-coil that can stimulate brain tissue deeper than a standard TMS coil. It is FDA-cleared for major depressive disorder (MDD) and for several other indications. A typical course is one session per day, five days a week, for about 4 to 6 weeks — roughly 36 sessions total.",
        "SAINT® — Stanford Accelerated Intelligent Neuromodulation Therapy — is delivered on the Magnus Medical SAINT Neuromodulation System. It uses a functional MRI (fMRI) scan to personalize the treatment target for each person, then delivers 10 sessions per day over 5 consecutive days, for about 50 sessions total. It is FDA-cleared for MDD in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
        "Both are legitimate, evidence-based options. The question is which fits your diagnosis, your schedule, and how much evidence you want behind the specific protocol you choose. Learn more about [[SAINT®|/saint]] and take the [[free candidacy screener|/am-i-a-candidate-screener]] to see which may apply to you.",
      ],
    },
    {
      heading: "How the deep TMS H-coil works",
      paragraphs: [
        "Deep TMS uses an H-coil — a treatment coil designed by BrainsWay — that is shaped to allow the magnetic field to reach deeper into the brain than a standard figure-8 coil. In standard TMS, the stimulation is strongest at the surface of the brain; with the H-coil, the field penetrates several centimeters deeper, which is why the protocol is called 'deep' TMS.",
        "For depression, the H-coil is positioned over the left dorsolateral prefrontal cortex (DLPFC) — the brain region involved in executive function and mood regulation. The patient sits in a chair while the coil delivers magnetic pulses for about 20 minutes per session. There is no anesthesia, no sedation, and no hospital stay.",
        "The BrainsWay H-coil system received FDA 510(k) clearance for major depressive disorder in adults who had not responded adequately to antidepressant medication. It has also received FDA clearances for additional indications. A full course typically runs 36 sessions over 4 to 6 weeks. If you want to compare it to standard TMS and other devices, see our [[TMS devices compared page|/learn/tms-devices-compared-saint-neurostar-brainsway-exomind]].",
      ],
    },
    {
      heading: "How SAINT® targeting works",
      paragraphs: [
        "SAINT® takes a different approach to targeting. Before treatment begins, each person has an fMRI scan that maps brain activity. The SAINT® system's targeting software identifies the specific spot on the DLPFC that is most anticorrelated with the subgenual anterior cingulate cortex (sgACC) — a brain region overactive in depression. In plain terms: the system finds the exact point on each person's prefrontal cortex that, when stimulated, is most likely to calm the overactive depression circuit.",
        "That target is different for every person. Standard TMS and deep TMS use a standard scalp measurement to find the DLPFC; SAINT® uses an individual brain scan. This is the core of the FDA-cleared SAINT® targeting system — the software that personalizes the coil placement based on the fMRI.",
        "Once the target is set, SAINT® delivers an accelerated schedule: 10 sessions per day for 5 consecutive days, using an intermittent theta-burst stimulation (iTBS) pulse pattern. The total is about 50 sessions in one week. Neuro Wellness is a Verified SAINT® Provider in Coral Springs, Florida. Learn more about [[what SAINT® is|/learn/what-is-saint-tms]] and [[what treatment-resistant depression means|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "Outcomes side by side: deep TMS vs SAINT®",
      paragraphs: [
        "Both deep TMS and SAINT® have published outcome data, but the evidence differs in size and design. Deep TMS has a longer published track record, including multi-site studies for depression. SAINT® has published randomized controlled trial data specifically for the SAINT® protocol on the Magnus system.",
        "In the 2022 randomized trial, 79% of participants reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. That figure comes from the SAINT® trial and applies to the SAINT® protocol — it does not transfer to deep TMS or any other accelerated TMS protocol. Ask any clinic for its own published outcome data for the specific protocol and device it uses.",
        "Deep TMS response and remission rates have been reported in peer-reviewed studies for the BrainsWay H-coil system. Those figures apply to the H-coil protocol studied, not to SAINT® or standard TMS. The comparison table below summarizes the key differences. For a deeper look at remission data, see our [[SAINT® TMS success rates page|/learn/saint-tms-success-rates]].",
      ],
      table: {
        headers: [
          "",
          "Deep TMS (BrainsWay H-coil)",
          "SAINT® (Magnus)",
          "Standard rTMS",
        ],
        rows: [
          [
            "Weeks of treatment",
            "4–6 weeks",
            "5 consecutive days",
            "4–6 weeks",
          ],
          ["Total sessions", "~36 (1/day)", "~50 (10/day)", "~36 (1/day)"],
          [
            "Targeting method",
            "Standard scalp measurement; H-coil reaches deeper tissue",
            "fMRI-guided, personalized per person",
            "Standard scalp measurement",
          ],
          [
            "FDA indications",
            "MDD and additional indications (H-coil)",
            "MDD in adults with inadequate response to prior antidepressants (K220177)",
            "MDD",
          ],
          [
            "Published remission data",
            "Peer-reviewed studies for the H-coil system",
            "79% remission in 2022 RCT (Cole et al.) — individual results may vary",
            "Established evidence base",
          ],
          [
            "Cost at NWCOA",
            "May be covered; coverage varies by plan",
            "Starts at $25,000; may be covered, varies by plan",
            "More often covered by insurance",
          ],
        ],
      },
    },
    {
      heading: "Time and cost: deep TMS vs SAINT®",
      paragraphs: [
        "Time is one of the biggest differences. Deep TMS and standard TMS both require daily visits for 4 to 6 weeks — about 36 sessions. If you work or live far from the clinic, that is a significant time commitment. SAINT® compresses the entire course into 5 consecutive days, with 10 sessions per day. You are in the clinic for most of the day during treatment week, but the course is over in one week instead of six.",
        "On cost: SAINT® at Neuro Wellness starts at $25,000. That price reflects the fMRI scan, the FDA-cleared targeting software, the Magnus system, physician oversight, and the full 50-session week. Deep TMS and standard TMS may be covered by insurance; coverage varies by plan. SAINT® may also be covered — coverage varies by plan. We verify your benefits for free, whatever carrier you have, so you know what your plan allows before you commit.",
        "Financing is available for SAINT® on a case-by-case basis and is discussed during your consultation. For the full cost breakdown, see our [[SAINT® TMS cost page|/learn/saint-tms-cost]]. To check what your plan covers, start with a [[free benefits verification|/check-insurance-options]].",
      ],
    },
    {
      heading: "Side effects: deep TMS vs SAINT®",
      paragraphs: [
        "Both deep TMS and SAINT® use magnetic pulses and share a similar side-effect profile. The most common side effects are headache, scalp discomfort or pain at the treatment site, and fatigue. These are usually mild and tend to ease as treatment progresses.",
        "Deep TMS with the H-coil can cause more discomfort at the treatment site early in the course because the coil stimulates a broader and deeper area. SAINT® uses an iTBS pulse pattern, which is generally well tolerated. Both protocols carry a small risk of seizure, and as with any depression treatment, a risk of worsening depression or suicidal thoughts.",
        "SAINT® is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. The same contraindications apply to deep TMS and standard TMS. If you are in crisis, call or text 988.",
      ],
    },
    {
      heading: "Who each protocol fits",
      paragraphs: [
        "Deep TMS may be a good fit for adults with depression who can commit to 4 to 6 weeks of daily visits and whose insurance covers it. Its broader FDA indications may also matter if you have a diagnosis beyond MDD that the H-coil is cleared for — though Neuro Wellness treats adults for depression with SAINT® and standard TMS.",
        "SAINT® fits adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication and who can commit to 5 consecutive days in the clinic. The compressed schedule suits people who want to avoid six weeks of daily visits and who want a protocol with fMRI-guided personalized targeting and published randomized trial data.",
        "Standard TMS remains the option with the largest evidence base and the most established insurance coverage. Its trade-off is time: 4 to 6 weeks of daily visits. Neuro Wellness offers SAINT® and standard TMS, along with SPRAVATO® (esketamine), IV ketamine, psychotherapy, and medication management. A [[free consultation|/contactus]] is the best way to learn which fits your diagnosis and circumstances.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.21060580",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression: 1-year follow-up. Am J Psychiatry. 2020;177(10):970–978.",
      url: "https://doi.org/10.1176/appi.ajp.2020.20040470",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification K220177 — Magnus Medical SAINT Neuromodulation System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — BrainsWay Deep TMS H-Coil System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Neuromodulation System — Breakthrough Device designation and FDA clearance.",
      url: "https://www.magnusmedical.com",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
      description:
        "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System: 10 sessions per day for 5 consecutive days.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Deep Transcranial Magnetic Stimulation (dTMS, BrainsWay H-coil)",
      description:
        "TMS delivered with a specialized H-coil that stimulates deeper brain tissue; FDA-cleared for major depressive disorder and additional indications.",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Devices Compared: SAINT®, NeuroStar, BrainsWay, Exomind",
      href: "/learn/tms-devices-compared-saint-neurostar-brainsway-exomind",
      description: "Side-by-side comparison of major TMS devices",
    },
    {
      title: "What Is Accelerated TMS?",
      href: "/what-is-accelerated-tms",
      description: "Complete guide to accelerated TMS protocols",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "Price, insurance, Medicare, and financing",
    },
    {
      title: "SAINT® TMS Success Rates",
      href: "/learn/saint-tms-success-rates",
      description: "Remission and response data explained",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate-screener",
      description: "Free candidacy screener",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick Answer", href: "#quick-answer-saint-vs-deep-tms" },
    { label: "How the H-Coil Works", href: "#how-the-deep-tms-h-coil-works" },
    { label: "How SAINT® Targeting Works", href: "#how-saint-targeting-works" },
    {
      label: "Outcomes Side by Side",
      href: "#outcomes-side-by-side-deep-tms-vs-saint",
    },
    { label: "Time and Cost", href: "#time-and-cost-deep-tms-vs-saint" },
    { label: "Side Effects", href: "#side-effects-deep-tms-vs-saint" },
    { label: "Who Each Fits", href: "#who-each-protocol-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
